import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Disclaimer from '../components/Disclaimer';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/content/contact`, form);
    } catch {}
    setSent(true);
    setLoading(false);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Contact — Elephants Answering</title>
        <meta name="description" content="Send Amy a message or request a short call. You're not alone in this." />
      </Helmet>
      <SiteNav />

      <main style={styles.main}>
        <div style={styles.inner}>

          <section style={styles.hero}>
            <p style={styles.eyebrow}>GET SUPPORT</p>
            <h1 style={styles.title}>You're not alone in this.</h1>
            <p style={styles.subtitle}>
              Use the form below to send Amy a note or request a short call. We read every message.
            </p>
          </section>

          <section style={styles.formSection}>
            {sent ? (
              <div style={styles.thanks}>
                <p style={styles.thanksTitle}>Message received.</p>
                <p style={styles.thanksBody}>I got your note. You're not alone in this. — Amy</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="contact-name">Your name</label>
                  <input
                    id="contact-name"
                    style={styles.input}
                    type="text"
                    placeholder="Name"
                    value={form.name}
                    onChange={set('name')}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="contact-email">Your email</label>
                  <input
                    id="contact-email"
                    style={styles.input}
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={set('email')}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    style={styles.textarea}
                    rows={7}
                    placeholder="What's on your mind? Request a call, ask a question, share what's happening — anything."
                    value={form.message}
                    onChange={set('message')}
                    required
                  />
                </div>
                <button style={styles.btn} type="submit" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </section>

          <section style={styles.alternativesSection}>
            <h2 style={styles.altTitle}>Other Ways to Reach Us</h2>
            <div style={styles.alts}>
              <div style={styles.altItem}>
                <p style={styles.altLabel}>Email</p>
                <a href="mailto:amy@elephantsanswering.com" style={styles.altLink}>amy@elephantsanswering.com</a>
              </div>
              <div style={styles.altItem}>
                <p style={styles.altLabel}>Instagram</p>
                <a href="https://www.instagram.com/elephantsanswering/" target="_blank" rel="noopener noreferrer" style={styles.altLink}>@elephantsanswering</a>
              </div>
              <div style={styles.altItem}>
                <p style={styles.altLabel}>If you are in immediate danger</p>
                <a href="tel:911" style={styles.altLink}>Call 911</a>
              </div>
              <div style={styles.altItem}>
                <p style={styles.altLabel}>National DV Hotline</p>
                <a href="tel:18007997233" style={styles.altLink}>1-800-799-SAFE (7233)</a>
              </div>
            </div>
          </section>

        </div>
      </main>

      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },
  main: { padding: '0 20px 80px' },
  inner: { maxWidth: '680px', margin: '0 auto' },

  hero: { backgroundColor: colors.navy, padding: '72px 40px', textAlign: 'center', margin: '0 -20px 64px' },
  eyebrow: { fontFamily: fonts.body, color: colors.peach, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '16px' },
  title: { fontFamily: fonts.heading, color: colors.white, fontSize: '38px', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '20px' },
  subtitle: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.7)', fontSize: '16px', lineHeight: '1.9', margin: '0 auto', maxWidth: '480px' },

  formSection: { marginBottom: '64px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: fonts.body, color: colors.navy, fontSize: '13px', fontWeight: '600', letterSpacing: '0.3px' },
  input: { padding: '12px 16px', border: `1px solid ${colors.peach}`, fontFamily: fonts.body, fontSize: '14px', color: colors.navy, backgroundColor: colors.white, outline: 'none' },
  textarea: { padding: '12px 16px', border: `1px solid ${colors.peach}`, fontFamily: fonts.body, fontSize: '14px', color: colors.navy, backgroundColor: colors.white, outline: 'none', resize: 'vertical' },
  btn: { alignSelf: 'flex-start', backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '14px 36px', fontSize: '14px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },

  thanks: { padding: '48px 40px', backgroundColor: colors.navy, textAlign: 'center' },
  thanksTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '22px', fontWeight: '600', marginBottom: '12px' },
  thanksBody: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.7)', fontSize: '15px', fontStyle: 'italic', lineHeight: '1.8', margin: 0 },

  alternativesSection: { borderTop: `1px solid ${colors.peach}`, paddingTop: '48px' },
  altTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '20px', fontWeight: '600', marginBottom: '28px', letterSpacing: '0.3px' },
  alts: { display: 'flex', flexDirection: 'column', gap: '20px' },
  altItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  altLabel: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 },
  altLink: { fontFamily: fonts.body, color: colors.rose, fontSize: '15px', textDecoration: 'none' },
};
